import { useCallback, useState } from "react";

import { useApi } from "@/hooks/useApi";

import type {
  APIResponse,
  MFAEnableRequest,
  PaginatedData,
  PINVerificationRequest,
  ResponseMeta,
  Workspace,
  WorkspaceDetailResponse,
  WorkspaceDetailSerializer,
  WorkspaceListResponse,
  WorkspaceListSerializer,
  WorkspaceMembership,
  WorkspaceMembershipPaginationData,
  WorkspaceUserProfile
} from "./types";
import { toast } from "react-toastify";
import { apiClient } from "@/utils/api-client";

// Workspace Hook
export const useWorkspace = () => {
  const { request, isLoading, error } = useApi();
  const [workspaces, setWorkspaces] = useState<WorkspaceListSerializer>();
  const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceDetailSerializer | null>(null);

  const getWorkspaces = useCallback(async () => {
    try {
      const response = await request<WorkspaceListResponse>("/api/workspaces/?page_size=100");
      if (response) {
        setWorkspaces(response);
        return response;
      }
    } catch (error) {
      console.error("Failed to get workspaces:", error);
      throw error;
    }
  }, [request]);

  const getWorkspace = useCallback(async (workspaceId: string) => {
    try {
      const response = await request<WorkspaceDetailResponse>(`/api/workspaces/${workspaceId}/`);
      if (response) {
        setCurrentWorkspace(response);
        return response;
      }
    } catch (error) {
      console.error("Failed to get workspace:", error);
      throw error;
    }
  }, [request]);

  const createWorkspace = useCallback(async (workspaceData: Partial<Workspace>) => {
    try {
      const formData = new FormData();
      Object.keys(workspaceData).forEach((key) => {
        const value = workspaceData[key];
        if (value instanceof File) {
          formData.append(key, value, value.name);
        } else {
          formData.append(key, value);
        }
      });

      const response = await request<WorkspaceDetailResponse>("/api/workspaces/", {
        method: "POST",
        body: workspaceData.logo ? formData : JSON.stringify(workspaceData),
      });

      if (response) {
        setWorkspaces(
          prev =>
            prev && Array.isArray(prev) ? [...prev, response] : [response]
        );
        return response;
      }
    } catch (error) {
      console.error("Failed to create workspace:", error);
      throw error;
    }
  }, [request]);

  const updateWorkspace = useCallback(async (workspaceId: string, workspaceData: Partial<Workspace>) => {
    try {
      const response = await request<WorkspaceDetailResponse>(`/api/workspaces/${workspaceId}/`, {
        method: "PATCH",
        body: JSON.stringify(workspaceData),
      });
      if (response) {
        setCurrentWorkspace(response);
        setWorkspaces(prev =>
          prev.map(w => w.id === workspaceId ? response : w)
        );
        return response;
      }
    } catch (error) {
      console.error("Failed to update workspace:", error);
      throw error;
    }
  }, [request]);

  const verifyPIN = useCallback(async (workspaceId: string, data: PINVerificationRequest) => {
    try {
      const response = await request<any>(`/api/workspaces/${workspaceId}/access/`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return response;
    } catch (error) {
      console.error("Failed to verify PIN:", error);
      throw error;
    }
  }, [request]);

  return {
    workspaces,
    currentWorkspace,
    isLoading,
    error,
    getWorkspaces,
    getWorkspace,
    createWorkspace,
    updateWorkspace,
    verifyPIN,
  };
};

// Workspace Profile Hook
export const useWorkspaceProfile = () => {
  const { request, isLoading, error } = useApi();
  const [profileData, setProfileData] = useState<any | null>(null);

  const getCurrentProfile = useCallback(async (workspaceId: string) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to get profile");
      return null;
    }

    try {
      const response = await request<APIResponse<any>>(`/api/workspaces/${workspaceId}/profiles/me/`);
      if (response) {
        setProfileData(response?.data);
        return response;
      }
    } catch (error) {
      console.error("Failed to get current workspace profile:", error);
      throw error;
    }
  }, [request]
  );

  const updateProfile = useCallback(async (workspaceId: string, profileData: any) => {
      if (!workspaceId) {
        console.error("Workspace ID is required to update profile");
        return null;
      }

      try {
        const response = await request<APIResponse<any>>(`/api/workspaces/${workspaceId}/profiles/me/`, {
          method: "PATCH",
          body: JSON.stringify(profileData),
        });

        if (response) {
          // Update local state with new profile data, preserving nested structure
          setProfileData(prev => {
            const newData = { ...prev };

            // Handle user_profile updates
            if (response?.data?.user_profile && prev?.user_profile) {
              newData.user_profile = {
                ...prev.user_profile,
                ...response.data.user_profile
              };
            } else if (response?.data?.user_profile) {
              newData.user_profile = response.data.user_profile;
            }

            // Handle workspace profile updates
            const { user_profile, ...workspaceProfileData } = response?.data || {};
            return { ...newData, ...workspaceProfileData };
          });

          return response;
        }
      } catch (error) {
        console.error("Failed to update workspace profile:", error);
        throw error;
      }
    }, [request]);

  // This function needs to be fixed to use the correct API path
  const uploadAvatar = useCallback(async (workspaceId: string, avatarFile: File, updateUserProfile: boolean = false) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to upload avatar");
      return null;
    }

    try {
      // Create a FormData object to properly send multipart/form-data
      const formData = new FormData();
      formData.append('avatar', avatarFile);
      formData.append('update_user_profile', updateUserProfile.toString());

      // Use fetch directly instead of the request utility to ensure proper handling of FormData
      const response = await apiClient.uploadFile<any>(
        `/api/workspaces/${workspaceId}/profiles/upload_avatar/`,
        avatarFile,
        'avatar',
        { update_user_profile: updateUserProfile.toString() }
      );
      console.log({v: "uploadavatar", response});

      if (response && response.data) {
        const data = response.data
        // Update the avatar URL in the local state
        setProfileData(prev => {
          if (!prev) return prev;

          const newData = { ...prev };
          if (data?.data?.avatar_url) {
            newData.avatar_url = data.data.avatar_url;
          }

          // Also update user profile avatar if needed
          if (updateUserProfile &&
              data?.data?.user_profile?.avatar_url &&
              newData.user_profile) {
            newData.user_profile = {
              ...newData.user_profile,
              avatar_url: data.data.user_profile.avatar_url
            };
          }

          return newData;
        });

        toast.success("Avatar updated successfully");
        return data;
      }
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      toast.error("Failed to upload avatar");
      throw error;
    }
  }, []);

  const uploadWorkspaceLogo = useCallback(async (workspaceId: string, logoFile: File) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to upload logo");
      return null;
    }

    try {
      // Create a FormData object
      const formData = new FormData();
      formData.append('logo', logoFile);

      // Use fetch directly instead of the request utility
      const response = await fetch(`/api/workspaces/${workspaceId}/profiles/upload_workspace_logo/`, {
        method: "POST",
        body: formData,
        // Do not set Content-Type header - browser will set it with boundary
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data) {
        toast.success("Workspace logo updated successfully");
        return data;
      }
    } catch (error) {
      console.error("Failed to upload workspace logo:", error);
      toast.error("Failed to upload workspace logo");
      throw error;
    }
  }, []);


  const enableMFA = useCallback(async (workspaceId: string, data: MFAEnableRequest) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to enable MFA");
      return null;
    }

    try {
      // FIXED API ENDPOINT: Use the correct path that matches the backend
      const response = await request<APIResponse<any>>(
        `/api/workspaces/${workspaceId}/profiles/me/enable_mfa/`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (response) {
        // Update MFA status in local state
        setProfileData(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            mfa_enabled: true,
            security_status: {
              ...prev.security_status,
              mfa_enabled: true,
              has_pin: true
            }
          };
        });

        toast.success("MFA enabled successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to enable MFA:", error);
      toast.error("Failed to enable MFA");
      throw error;
    }
  }, [request]);

  const verifyPIN = useCallback(async (workspaceId: string, data: PINVerificationRequest) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to verify PIN");
      return null;
    }

    try {
      // FIXED API ENDPOINT: Use the correct path that matches the backend
      const response = await request<APIResponse<any>>(
        `/api/workspaces/${workspaceId}/profiles/verify_pin/`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      return response;
    } catch (error) {
      console.error("Failed to verify PIN:", error);
      throw error;
    }
  }, [request]);

  const changePIN = useCallback(async (workspaceId: string, currentPin: string, newPin: string) => {
    if (!workspaceId) {
      console.error("Workspace ID is required to change PIN");
      return null;
    }

    try {
      // This endpoint should be added to match the new backend
      const response = await request<APIResponse<any>>(
        `/api/workspaces/${workspaceId}/profiles/change_pin/`,
        {
          method: "POST",
          body: JSON.stringify({
            current_pin: currentPin,
            new_pin: newPin
          }),
        }
      );

      if (response) {
        toast.success("PIN changed successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to change PIN:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to change PIN. Please try again."
      );
      throw error;
    }
  }, [request]);
  return {
    profileData,
    isLoading,
    error,
    getCurrentProfile,
    updateProfile,
    uploadAvatar,
    uploadWorkspaceLogo,
    enableMFA,
    verifyPIN,
    changePIN,
  };
};

// Workspace Membership Hook
export const useWorkspaceMembership = () => {
  const { request, isLoading, error } = useApi();
  const [members, setMembers] = useState<WorkspaceMembership[]>([]);
  const [paginatedMembers, setPaginatedMembers] = useState<WorkspaceMembershipPaginationData[] | null>(null);
  const [paginationMembersMeta, setPaginationMembersMeta] = useState<ResponseMeta | null>(null);
  const [allMembers, setAllMembers] = useState<WorkspaceMembershipPaginationData[] | null>(null);

  // Original getMembers function for single page (if needed)
  const getMembers = useCallback(async (workspaceId: string, page: number = 1) => {
    try {
      const response = await request<WorkspaceMembershipPaginationData[]>(`/api/workspaces/${workspaceId}/members/?page=${page}`);
      if (response) {
        const emailAdaptedPaginatedMembers = response.data?.map((member) => {
          return { ...member, user_email: member.read_user_email }
        })
        setPaginatedMembers(emailAdaptedPaginatedMembers ?? response.data);
        setPaginationMembersMeta(response.meta);
        return response;
      }
    } catch (error) {
      console.error("Failed to get workspace members:", error);
      throw error;
    }
  }, [request]);

  // New function to get ALL members from all pages
  const getAllMembers = useCallback(async (workspaceId: string) => {
    try {
      let allMembersData: WorkspaceMembershipPaginationData[] = [];
      let currentPage = 1;
      let hasNextPage = true;
      let finalMeta: ResponseMeta | null = null;

      while (hasNextPage) {
        const response = await request<WorkspaceMembershipPaginationData[]>(
          `/api/workspaces/${workspaceId}/members/?page=${currentPage}&page_size=100`
        );

        if (response && response.data) {
          // Adapt email field for each member
          const emailAdaptedMembers = response.data.map((member) => ({
            ...member,
            user_email: member.read_user_email
          }));

          // Add to accumulated data
          allMembersData = [...allMembersData, ...emailAdaptedMembers];

          // Store the final metadata
          finalMeta = response.meta;

          // Check if there's a next page
          hasNextPage = !!response.meta?.pagination?.next;
          currentPage += 1;

          // Safety check to prevent infinite loops
          if (currentPage > 100) {
            console.warn("Reached maximum page limit (100) while fetching members");
            break;
          }
        } else {
          hasNextPage = false;
        }
      }

      // Update state with all members
      setAllMembers(allMembersData);
      setPaginatedMembers(allMembersData);

      // Update meta with total count
      if (finalMeta) {
        setPaginationMembersMeta({
          ...finalMeta,
          pagination: {
            ...finalMeta.pagination,
            count: allMembersData.length,
            current_page: 1,
            page_size: allMembersData.length,
            next: null,
            previous: null
          }
        });
      }

      return {
        data: allMembersData,
        meta: finalMeta,
        totalCount: allMembersData.length
      };

    } catch (error) {
      console.error("Failed to get all workspace members:", error);
      throw error;
    }
  }, [request]);

  // Alternative approach: Parallel fetching (faster but more aggressive)
  const getAllMembersParallel = useCallback(async (workspaceId: string) => {
    try {
      // First, get the first page to determine total count
      const firstPageResponse = await request<WorkspaceMembershipPaginationData[]>(
        `/api/workspaces/${workspaceId}/members/?page=1&page_size=100`
      );

      if (!firstPageResponse || !firstPageResponse.data) {
        throw new Error("Failed to fetch first page");
      }

      const totalCount = firstPageResponse.meta?.pagination?.count || 0;
      const pageSize = firstPageResponse.meta?.pagination?.page_size || 100;
      const totalPages = Math.ceil(totalCount / pageSize);

      // If there's only one page, return early
      if (totalPages <= 1) {
        const emailAdaptedMembers = firstPageResponse.data.map((member) => ({
          ...member,
          user_email: member.read_user_email
        }));

        setAllMembers(emailAdaptedMembers);
        setPaginatedMembers(emailAdaptedMembers);
        setPaginationMembersMeta(firstPageResponse.meta);

        return {
          data: emailAdaptedMembers,
          meta: firstPageResponse.meta,
          totalCount: emailAdaptedMembers.length
        };
      }

      // Create array of promises for remaining pages
      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(
          request<WorkspaceMembershipPaginationData[]>(
            `/api/workspaces/${workspaceId}/members/?page=${page}&page_size=100`
          )
        );
      }

      // Wait for all pages to complete
      const remainingPages = await Promise.all(pagePromises);

      // Combine all data
      let allMembersData = firstPageResponse.data.map((member) => ({
        ...member,
        user_email: member.read_user_email
      }));

      remainingPages.forEach(pageResponse => {
        if (pageResponse && pageResponse.data) {
          const emailAdaptedMembers = pageResponse.data.map((member) => ({
            ...member,
            user_email: member.read_user_email
          }));
          allMembersData = [...allMembersData, ...emailAdaptedMembers];
        }
      });

      // Update state
      setAllMembers(allMembersData);
      setPaginatedMembers(allMembersData);
      setPaginationMembersMeta({
        ...firstPageResponse.meta,
        pagination: {
          ...firstPageResponse.meta.pagination,
          count: allMembersData.length,
          current_page: 1,
          page_size: allMembersData.length,
          next: null,
          previous: null
        }
      });

      return {
        data: allMembersData,
        meta: firstPageResponse.meta,
        totalCount: allMembersData.length
      };

    } catch (error) {
      console.error("Failed to get all workspace members (parallel):", error);
      throw error;
    }
  }, [request]);

  const addMember = useCallback(async (workspaceId: string, memberData: Partial<WorkspaceMembership>) => {
    try {
      const response = await request<APIResponse<WorkspaceMembership>>(`/api/workspaces/${workspaceId}/members/`, {
        method: "POST",
        body: JSON.stringify(memberData),
      });
      if (response) {
        console.log('res', response)
        setMembers(prev => [...prev, response]);
        // Refresh all members after adding
        getAllMembers(workspaceId);
        return response;
      }
    } catch (error) {
      console.error("Failed to add workspace member:", error);
      throw error;
    }
  }, [request, getAllMembers]);

  const updateMember = useCallback(async (workspaceId: string, memberId: string, memberData: Partial<WorkspaceMembership>) => {
    try {
      const response = await request<APIResponse<WorkspaceMembership>>(`/api/workspaces/${workspaceId}/members/${memberId}/`, {
        method: "PATCH",
        body: JSON.stringify(memberData),
      });
      if (response) {
        setMembers(prev =>
          prev.map(m => m.id === memberId ? response : m)
        );
        // Refresh all members after updating
        getAllMembers(workspaceId);
        return response;
      }
    } catch (error) {
      console.error("Failed to update workspace member:", error);
      throw error;
    }
  }, [request, getAllMembers]);

  const updateMemberRole = useCallback(async (workspaceId: string, memberId: string, memberData: Partial<WorkspaceMembership>) => {
    try {
      const response = await request<APIResponse<WorkspaceMembership>>(`/api/workspaces/${workspaceId}/members/${memberId}/update-role/`, {
        method: "PATCH",
        body: JSON.stringify(memberData),
      });
      if (response) {
        setMembers(prev =>
          prev.map(m => m.id === memberId ? response : m)
        );
        // Refresh all members after role update
        getAllMembers(workspaceId);
        return response;
      }
    } catch (error) {
      console.error("Failed to update workspace member:", error);
      throw error;
    }
  }, [request, getAllMembers]);

  const removeMember = useCallback(async (workspaceId: string, memberId: string) => {
    try {
      await request(`/api/workspaces/${workspaceId}/members/${memberId}/`, {
        method: "DELETE",
      });
      setMembers(prev => prev.filter(m => m.id !== memberId));
      // Refresh all members after removal
      getAllMembers(workspaceId);
    } catch (error) {
      console.error("Failed to remove workspace member:", error);
      throw error;
    }
  }, [request, getAllMembers]);

  return {
    members,
    paginatedMembers,
    allMembers,
    paginationMembersMeta,
    isLoading,
    error,
    getMembers, // Original function for single page
    getAllMembers, // New function for all pages (sequential)
    getAllMembersParallel, // Alternative parallel approach
    addMember,
    updateMember,
    updateMemberRole,
    removeMember,
  };
};